<template>
	<NuxtLayout
		name="detail"
		title="Functional Conditioning"
		description="Tijdens onze Functional Conditioning lessen werk je aan je conditie, krachtuithoudingsvermogen en functionele bewegingspatronen. Een energieke training voor iedereen die fitter wil worden, op een manier die aansluit bij het dagelijks leven."
		:breadcrumbs
		:button="{
			text: 'Informatie aanvragen',
			to: {
				url: '#aanmelden',
				title: 'Small Group Training informatie aanvragen',
			},
			iconRight: 'fa-envelope',
		}"
	>
		<div class="container max-w-screen-md flex flex-col gap-24 md:gap-36 py-36">
			<div class="flex flex-col gap-8">
				<AtomTitle
					tag="h2"
					size="2xl"
				>
					Wat is Functional Conditioning?
				</AtomTitle>
				<p>
					Functional Conditioning is een energieke, uitdagende trainingsvorm waarbij je werkt aan je conditie, kracht en functionele fitheid. De training combineert intensieve cardioblokken met krachtuithoudingsoefeningen in een hoog tempo, afgestemd op realistische bewegingen die je lichaam sterker en efficiënter maken. Functional Conditioning is onderdeel van het
					<AtomLink
						url="/small-group-training"
						title="Small Group Training"
						class="link"
					>
						Small Group Training lidmaatschap
					</AtomLink>
					en perfect voor wie fysiek uitgedaagd wil worden én beter wil presteren in het dagelijks leven.
				</p>
			</div>
		</div>
		<div class="container grid sm:grid-cols-2 gap-8">
			<AtomImage
				src="/img/fitness/battle-ropes_01.jpg"
				alt="Battle Rope workout in Small Group Training"
				img-class="rounded aspect-video object-cover"
				:width="1220"
				:height="686"
			/>
			<AtomImage
				src="/img/fitness/small-group-training_03.jpg"
				alt="Airbike en Roeien in Small Group Training"
				img-class="rounded aspect-video object-cover"
				:width="1220"
				:height="686"
			/>
		</div>
		<div class="container max-w-screen-md flex flex-col gap-24 md:gap-36 py-36">
			<div class="flex flex-col gap-8">
				<AtomTitle
					tag="h2"
					size="2xl"
				>
					Voor wie is deze training geschikt?
				</AtomTitle>
				<p>
					Functional Conditioning is geschikt voor iedereen met een basisconditie die op zoek is naar een intensieve en doelgerichte training. De intensiteit ligt hoger dan bij onze
					<AtomLink
						url="/small-group-training/lifefit-functionele-begeleide-fitness"
						title="LifeFit functionele begeleide fitness"
						class="link"
					>
						LifeFit
					</AtomLink>
					lessen, en maakt deze training bijzonder geschikt voor wie zichzelf fysiek wil uitdagen, grenzen wil verleggen en effectief wil werken aan vetverbranding, uithoudingsvermogen en kracht. Zoek je een motiverende groepsles waarin je echt stappen zet? Dan is dit jouw training.
				</p>
			</div>
			<div class="flex flex-col gap-8">
				<AtomTitle
					tag="h2"
					size="2xl"
				>
					Wat kun je verwachten?
				</AtomTitle>
				<p>
					Elke training duurt 60 minuten en vindt plaats in een kleine groep van maximaal zes deelnemers. Je traint onder begeleiding van een professionele coach die oog heeft voor jouw techniek, tempo en niveau.
				</p>
				<p>We starten elke training met een gevarieerde warming-up om je lichaam voor te bereiden op inspanning, en sluiten af met een dynamische stretchsessie gericht op mobiliteit en flexibiliteit.</p>
				<p>De trainingen zijn gevarieerd en je kunt onder andere het volgende verwachten:</p>
				<MoleculeIconList
					class="md:columns-2"
					:items="[
						'Intervaltraining',
						'Lichaamsgewichtoefeningen',
						'Lichte tot matige gewichten',
						'Sled oefeningen',
						'Kettlebell en dumbell oefeningen',
						'Battle ropes',
						'Airbike en cardio elementen',
					]"
				/>
			</div>
			<div class="flex justify-center gap-8">
				<AtomButton
					size="lg"
					:variant="ButtonVariant.Green"
					icon-right="fa-envelope"
					text="Informatie aanvragen"
					:to="{
						url: '#aanmelden',
						title: 'Small Group Training informatie aanvragen',
					}"
					@click="trackButtonClick('Page_FunctionalConditioning_CTA_Lead')"
				/>
				<AtomButton
					size="lg"
					:variant="ButtonVariant.DarkBorder"
					icon-right="fa-bolt"
					text="Meedoen met een gratis training"
					:to="{
						url: '/small-group-training/aanmelden-proefles',
						title: 'Aanmelden voor een Small Group Training proefles',
					}"
				/>
			</div>
			<div class="flex flex-col gap-8">
				<AtomTitle
					tag="h2"
					size="2xl"
				>
					Waarom trainen bij Fit Hier?
				</AtomTitle>
				<p>
					Bij Fit Hier in Uden train je in een exclusieve setting, zonder drukte of rijen bij apparaten. Dankzij onze kleinschalige aanpak en persoonlijke begeleiding krijg je de aandacht die nodig is om echt progressie te boeken. Onze trainers zijn deskundig, betrokken en motiveren je om net dat beetje extra te geven, elke training opnieuw.
				</p>
			</div>
			<div class="flex flex-col gap-4">
				<AtomTitle
					tag="h2"
					size="2xl"
				>
					Informatie aanvragen
				</AtomTitle>
				<p>
					Wil je meer weten over het Small Group Training lidmaatschap? Vraag het informatiepakket aan en je ontvangt binnen enkele minuten alle details over de trainingen, het rooster en de tarieven per mail.
				</p>
			</div>
			<AtomCard
				id="aanmelden"
				class="p-12 scroll-mt-36"
			>
				<OrganismFormLeadSgt />
			</AtomCard>

			<div class="flex flex-col gap-8">
				<AtomTitle
					tag="h2"
					size="2xl"
					class="text-center"
				>
					Ervaringen van leden
				</AtomTitle>
				<OrganismReviewSlideshow :reviews="reviews" />
			</div>

			<div class="flex flex-col gap-16">
				<AtomTitle
					tag="h2"
					size="2xl"
					class="text-center"
				>
					Veelgestelde vragen
				</AtomTitle>
				<OrganismFaq
					:items="functionalConditioningFaqItems"
				/>
			</div>
		</div>
	</NuxtLayout>
</template>

<script setup lang="ts">
import type { BreadcrumbsType } from '#base/core/types/BreadcrumbsType';
import { ButtonVariant } from '#base/core/enums/ButtonVariantEnum';
import reviews from '#fit/core/consts/reviews';
import { functionalConditioningFaqItems } from '#fit/core/consts/faqItems';

useSeoMeta({
	title: 'Functional Conditioning - Conditietraining Uden | Fit Hier',
	ogTitle: 'Small Group Conditietraining bij Fit Hier Uden',
	description: 'Verbeter je conditie en kracht met Functional Conditioning Small Group Training in Uden. Maximaal 6 deelnemers, persoonlijke coaching. Boek je gratis proefles!',
});

const breadcrumbs: BreadcrumbsType = [
	{
		text: 'Home',
		to: {
			title: 'Home',
			url: '/',
		},
	},
	{
		text: 'Small Group Training',
		to: {
			url: '/small-group-training',
			title: 'Small Group Training',
		},
	},
	{
		text: 'Functional Conditioning',
		to: {
			url: '/small-group-training/functional-conditioning-conditietraining',
			title: 'Functional Conditioning',
		},
	},
];

const { trackButtonClick } = useEventTracking();
</script>
